import { api } from "services/axios/api";
import { IEmpresa } from "shared/interfaces/empresa";

export interface IFSelect {
  value: number;
  label: string;
}

export interface ICodigoBase {
  idComponenteBase: number;
  codigoBase: string;
  descricao: string;
  grupo: string;
  possuiAplicabilidade: boolean;
  qtdeAplicaveis: number;
  cor?: string;
  referencia?: string;
  situacao?: number;
  // FASE 03 (item 1.B): true quando há vínculo (ativo OU tombstone pendente) — governa a
  // exibição da coluna Situação no grid, independente do ícone verde (possuiAplicabilidade).
  possuiSituacao?: boolean;
  idStatusComponente?: number;
  icone?: string;
  fichaTecnica?: string;
}

export interface ILinhaSistemaPadrao {
  idLinhaSistema: number;
  isPadrao: boolean;
}

export interface IComponenteAgrupado {
  idAplicabilidadeAgrupamento: number;
  idComponenteAplicavel: number;
  codigoAplicavel: string;
  descricaoAplicavel: string;
  iconeAplicavel?: string;
  corAplicavel?: string;
  referenciaAplicavel?: string;
  fichaTecnicaAplicavel?: string;
}

export interface IApontamento {
  idAplicabilidade: number;
  idComponenteBase: number;
  codigoBase: string;
  descricaoBase: string;
  idComponenteAplicavel: number;
  codigoAplicavel: string;
  descricaoAplicavel: string;
  iconeAplicavel?: string;
  corAplicavel?: string;
  referenciaAplicavel?: string;
  fichaTecnicaAplicavel?: string;
  isPadrao: boolean;
  linhasSistema?: ILinhaSistemaPadrao[];
  componentesAgrupados?: IComponenteAgrupado[];
  componentesBaseAgrupados?: IComponenteBaseAgrupado[];
  observacao?: string;
  situacao: number;
  // TAREFA 11.6 (FASE 03): tombstone — apontamento excluído pelo usuário, ainda em ciclo.
  excluida?: boolean;
}

export interface ISalvarApontamento {
  idAplicabilidade?: number;
  idEmpresa: number;
  idComponenteBase: number;
  idComponenteAplicavel: number;
  isPadrao: boolean;
  linhasSistema?: ILinhaSistemaPadrao[];
  observacao?: string;
}

export interface IEstadoAplicabilidade {
  situacao: number;
  totalCodigosBase: number;
  codigosBaseComAplicabilidade: number;
  percentualCobertura: number;
  temAplicabilidadesEmEdicao: boolean;
  temAplicabilidadesEmHomologacao: boolean;
  temAplicabilidadesHomologadas: boolean;
}

export interface IComponenteAplicavel {
  idComponente: number;
  codigo: string;
  descricao: string;
  grupo: string;
  cor?: string;
  icone?: string;
  referencia?: string;
  fichaTecnica?: string;
}

export interface IListarCodigosBaseParams {
  idEmpresa: number;
  page?: number;
  limit?: number;
  sortBy?: string;
  direction?: string;
  filtrar?: string;
  filtroBusca?: string;
  idGrupo?: number;
  idTipologia?: number;
  idLinhaSistema?: number;
  idProjetista?: number;
  somenteComAplicabilidade?: boolean;
  situacao?: number;
  filtroCodigoBase?: string;
  filtroCor?: string;
  filtroDescricao?: string;
  filtroReferencia?: string;
  incluirDescontinuados?: boolean;
  // R-01 (Plano Filtro Tipologia): tela CADASTRO envia true para restringir ao subconjunto
  // de componentes presentes em TipologiaComponentes. Demais consumidores omitem.
  restringirEmTipologias?: boolean;
}

export const listarCodigosBase = async (params: IListarCodigosBaseParams) => {
  const queryParams = new URLSearchParams();
  queryParams.append("idEmpresa", params.idEmpresa.toString());
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.sortBy) queryParams.append("sortBy", params.sortBy);
  if (params.direction) queryParams.append("direction", params.direction);
  if (params.filtrar) queryParams.append("filtrar", params.filtrar);
  if (params.filtroBusca) queryParams.append("filtroBusca", params.filtroBusca);
  if (params.idGrupo) queryParams.append("idGrupo", params.idGrupo.toString());
  if (params.idTipologia) queryParams.append("idTipologia", params.idTipologia.toString());
  if (params.idLinhaSistema) queryParams.append("idLinhaSistema", params.idLinhaSistema.toString());
  if (params.idProjetista) queryParams.append("idProjetista", params.idProjetista.toString());
  if (params.somenteComAplicabilidade !== undefined) queryParams.append("somenteComAplicabilidade", params.somenteComAplicabilidade.toString());
  if (params.situacao !== undefined) queryParams.append("situacao", params.situacao.toString());
  if (params.filtroCodigoBase) queryParams.append("filtroCodigoBase", params.filtroCodigoBase);
  if (params.filtroCor) queryParams.append("filtroCor", params.filtroCor);
  if (params.filtroDescricao) queryParams.append("filtroDescricao", params.filtroDescricao);
  if (params.filtroReferencia) queryParams.append("filtroReferencia", params.filtroReferencia);
  if (params.incluirDescontinuados !== undefined) queryParams.append("incluirDescontinuados", params.incluirDescontinuados.toString());
  if (params.restringirEmTipologias !== undefined) queryParams.append("restringirEmTipologias", params.restringirEmTipologias.toString());

  const response = await api.get<{ response: { items: ICodigoBase[]; page: number; totalRecords: number; totalPages: number } }>(
    `/Aplicabilidade/ListarCodigosBase?${queryParams.toString()}`
  );
  // O CustomResponse retorna { response: {...} }, então precisamos acessar response.response
  return { data: response.data.response || response.data };
};

export interface IEmpresaComAplicabilidade {
  idEmpresa: number;
  nomeFantasia: string;
}

// Universo do combo "Aplicabilidade Utilizada" do Cadastro de Projetista:
// todas as empresas com apontamento de aplicabilidade vivo (decisão P2 de 28/07/2026).
export const getEmpresasComAplicabilidade = async (): Promise<IEmpresaComAplicabilidade[]> => {
  try {
    const response = await api.get<any>(
      "/Aplicabilidade/ListarEmpresasComAplicabilidade"
    );
    // FASE 05 — 6ª validação/PO 2026-08-11: o MainController.CustomResponse devolve o array
    // DIRETO (Ok(result)), sem o envelope { response: ... }. Sem este fallback (como nas demais
    // funções deste service) o combo "Aplicabilidade Utilizada" vinha sempre vazio ("Nenhuma").
    const d: any = response.data;
    return Array.isArray(d?.response) ? d.response : (Array.isArray(d) ? d : []);
  } catch (error) {
    console.error("[API ERROR] ListarEmpresasComAplicabilidade", error);
    return [];
  }
};

export const obterApontamentos = async (idEmpresa: number, idComponenteBase: number) => {
  const response = await api.get<{ response: IApontamento[] }>(
    `/Aplicabilidade/ObterApontamentos?idEmpresa=${idEmpresa}&idComponenteBase=${idComponenteBase}`
  );
  // O CustomResponse retorna { response: {...} }, então precisamos acessar response.response
  return { data: response.data.response || response.data };
};

export const obterEstado = async (idEmpresa: number, params?: {
  filtroBusca?: string;
  idGrupo?: number;
  idTipologia?: number;
  idLinhaSistema?: number;
  idProjetista?: number;
  somenteComAplicabilidade?: boolean;
  // R-01 (Plano Filtro Tipologia): paridade com listarCodigosBase para que numerador/denominador
  // do % cobertura usem o mesmo universo do grid quando a tela CADASTRO envia true.
  restringirEmTipologias?: boolean;
}) => {
  const queryParams = new URLSearchParams();
  queryParams.append("idEmpresa", idEmpresa.toString());
  if (params?.filtroBusca) queryParams.append("filtroBusca", params.filtroBusca);
  if (params?.idGrupo) queryParams.append("idGrupo", params.idGrupo.toString());
  if (params?.idTipologia) queryParams.append("idTipologia", params.idTipologia.toString());
  if (params?.idLinhaSistema) queryParams.append("idLinhaSistema", params.idLinhaSistema.toString());
  if (params?.idProjetista) queryParams.append("idProjetista", params.idProjetista.toString());
  if (params?.somenteComAplicabilidade !== undefined) queryParams.append("somenteComAplicabilidade", params.somenteComAplicabilidade.toString());
  if (params?.restringirEmTipologias !== undefined) queryParams.append("restringirEmTipologias", params.restringirEmTipologias.toString());

  const response = await api.get<{ response: IEstadoAplicabilidade }>(
    `/Aplicabilidade/ObterEstado?${queryParams.toString()}`
  );
  // O CustomResponse retorna { response: {...} }, então precisamos acessar response.response
  return { data: response.data.response || response.data };
};

export const salvarApontamento = async (apontamento: ISalvarApontamento) => {
  const response = await api.post<{ data: number }>(
    `/Aplicabilidade/SalvarApontamento`,
    apontamento
  );
  return response.data;
};

export const removerApontamento = async (idAplicabilidade: number) => {
  const response = await api.delete<{ data: boolean }>(
    `/Aplicabilidade/RemoverApontamento?idAplicabilidade=${idAplicabilidade}`
  );
  return response.data;
};

export const adicionarComponenteAgrupado = async (idAplicabilidade: number, idComponenteAplicavel: number) => {
  const response = await api.post<{ response: number }>(
    `/Aplicabilidade/AdicionarComponenteAgrupado?idAplicabilidade=${idAplicabilidade}&idComponenteAplicavel=${idComponenteAplicavel}`
  );
  return response.data.response || response.data;
};

export const removerComponenteAgrupado = async (idAplicabilidadeAgrupamento: number) => {
  const response = await api.delete<{ response: boolean }>(
    `/Aplicabilidade/RemoverComponenteAgrupado?idAplicabilidadeAgrupamento=${idAplicabilidadeAgrupamento}`
  );
  return response.data.response || response.data;
};

export interface IComponenteBaseAgrupado {
  idAplicabilidadeAgrupamentoBase: number;
  idComponenteBase: number;
  codigoBase: string;
  descricaoBase: string;
  iconeBase?: string;
  corBase?: string;
  referenciaBase?: string;
  // P-10 (Resposta PO 2026-05-12): link da ficha técnica da base agrupada.
  fichaTecnicaBase?: string;
}

export const adicionarComponenteBaseAgrupado = async (idAplicabilidade: number, idComponenteBase: number) => {
  const response = await api.post<{ response: number }>(
    `/Aplicabilidade/AdicionarComponenteBaseAgrupado?idAplicabilidade=${idAplicabilidade}&idComponenteBase=${idComponenteBase}`
  );
  return response.data.response || response.data;
};

export const removerComponenteBaseAgrupado = async (idAplicabilidadeAgrupamentoBase: number) => {
  const response = await api.delete<{ response: boolean }>(
    `/Aplicabilidade/RemoverComponenteBaseAgrupado?idAplicabilidadeAgrupamentoBase=${idAplicabilidadeAgrupamentoBase}`
  );
  return response.data.response || response.data;
};

export const alterarEstado = async (idEmpresa: number, novaSituacao: number) => {
  const response = await api.put<{ response: number }>(
    `/Aplicabilidade/AlterarEstado?idEmpresa=${idEmpresa}&novaSituacao=${novaSituacao}`
  );
  // CustomResponse retorna { response: <number> }
  return (response.data as any)?.response ?? (response.data as any);
};

export const listarComponentesAplicaveis = async (idEmpresa: number, idComponenteBase: number, filtroBusca?: string, idGrupo?: number, page: number = 1, pageSize: number = 10, filtroGrupoDescricao?: string) => {
  const queryParams = new URLSearchParams();
  queryParams.append("idEmpresa", idEmpresa.toString());
  queryParams.append("idComponenteBase", idComponenteBase.toString());
  if (filtroBusca) queryParams.append("filtroBusca", filtroBusca);
  if (idGrupo) queryParams.append("idGrupo", idGrupo.toString());
  if (filtroGrupoDescricao) queryParams.append("filtroGrupoDescricao", filtroGrupoDescricao);
  queryParams.append("page", page.toString());
  queryParams.append("pageSize", pageSize.toString());

  const response = await api.get<{ items: IComponenteAplicavel[]; totalRecords: number; totalPages: number; page: number }>(
    `/Aplicabilidade/ListarComponentesAplicaveis?${queryParams.toString()}`
  );
  const data = (response.data as any).response || response.data;
  return { data: { items: data.items || [], totalRecords: data.totalRecords || 0, totalPages: data.totalPages || 0, page: data.page || 1 } };
};

// TAREFA 05 (FASE 03): lista todos os grupos distintos dos componentes apontáveis para a base,
// para popular o dropdown Grupo do modal independentemente da paginação do grid.
export const listarGruposComponentesAplicaveis = async (
  idEmpresa: number,
  idComponenteBase: number
): Promise<string[]> => {
  const queryParams = new URLSearchParams();
  queryParams.append("idEmpresa", idEmpresa.toString());
  queryParams.append("idComponenteBase", idComponenteBase.toString());

  const response = await api.get<{ response: string[] }>(
    `/Aplicabilidade/ListarGruposComponentesAplicaveis?${queryParams.toString()}`
  );
  const data = (response.data as any).response ?? response.data;
  return Array.isArray(data) ? data : [];
};

// FASE 03 (item 5): lista os grupos distintos dos CÓDIGOS BASE elegíveis da empresa, para o
// dropdown Grupo do modal de agrupamento de bases — independente da paginação server-side do grid.
export interface IGrupoCodigoBase {
  idGrupo: number;
  descricao: string;
}

export const listarGruposCodigosBase = async (idEmpresa: number): Promise<IGrupoCodigoBase[]> => {
  const queryParams = new URLSearchParams();
  queryParams.append("idEmpresa", idEmpresa.toString());

  const response = await api.get<{ response: IGrupoCodigoBase[] }>(
    `/Aplicabilidade/ListarGruposCodigosBase?${queryParams.toString()}`
  );
  const data = (response.data as any).response ?? response.data;
  return Array.isArray(data) ? data : [];
};

export const baixarPlanilhaModeloXlsx = async (): Promise<Blob> => {
  const response = await api.get(`/Aplicabilidade/DownloadPlanilhaModelo`, {
    responseType: "blob",
  });
  return response.data as Blob;
};

export const importarLoteAplicabilidades = async (idEmpresa: number, arquivo: File) => {
  const formData = new FormData();
  formData.append("arquivo", arquivo);

  const response = await api.post<{ response: any }>(
    `/Aplicabilidade/ImportarLote?idEmpresa=${idEmpresa}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data.response || response.data;
};

export const exportarPlanilhaAplicabilidades = async (
  params: IListarCodigosBaseParams
) => {
  const queryParams = new URLSearchParams();
  queryParams.append("idEmpresa", params.idEmpresa.toString());
  if (params.filtroBusca) queryParams.append("filtroBusca", params.filtroBusca);
  if (params.idGrupo) queryParams.append("idGrupo", params.idGrupo.toString());
  if (params.idTipologia) queryParams.append("idTipologia", params.idTipologia.toString());
  if (params.idLinhaSistema) queryParams.append("idLinhaSistema", params.idLinhaSistema.toString());
  if (params.idProjetista) queryParams.append("idProjetista", params.idProjetista.toString());
  if (params.somenteComAplicabilidade !== undefined) queryParams.append("somenteComAplicabilidade", params.somenteComAplicabilidade.toString());
  if (params.situacao !== undefined) queryParams.append("situacao", params.situacao.toString());
  if (params.filtroCodigoBase) queryParams.append("filtroCodigoBase", params.filtroCodigoBase);
  if (params.filtroCor) queryParams.append("filtroCor", params.filtroCor);
  if (params.filtroDescricao) queryParams.append("filtroDescricao", params.filtroDescricao);
  if (params.filtroReferencia) queryParams.append("filtroReferencia", params.filtroReferencia);
  if (params.restringirEmTipologias !== undefined) queryParams.append("restringirEmTipologias", params.restringirEmTipologias.toString());

  const response = await api.get(
    `/Aplicabilidade/ExportarPlanilha?${queryParams.toString()}`,
    { responseType: "blob" }
  );
  return response.data as Blob;
};

export const listarProjetistas = async (): Promise<IFSelect[]> => {
  try {
    const response = await api.get<any>("/Projetista/ListarProjetistas", {
      params: { page: 1, limit: 1000 },
    });

    const data = response?.data?.response || response?.data;
    const items = (data?.items ?? data?.Items ?? data) as any;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items
      .filter((p: any) => p?.ativo !== false)
      .map((projetista: any) => ({
        value: Number(projetista?.idProjetista ?? projetista?.IdProjetista ?? projetista?.id),
        label: String(projetista?.nome ?? projetista?.Nome ?? "").trim(),
      }))
      .filter((item: IFSelect) => !Number.isNaN(item.value) && item.value > 0 && !!item.label);
  } catch {
    return [];
  }
};

export const listarLinhasSistema = async (
  projetistaId?: number,
  idEmpresa?: number,
  restringirPorProjetistasEmpresa?: boolean
): Promise<IFSelect[]> => {
  try {
    // FASE 03 (item 10): guarda defensiva — sem idEmpresa válido o backend rejeita com HTTP 400
    // (ObterConfiguracaoConsultaQueryValidator exige IdEmpresa > 0). Evita a chamada inútil.
    if (!idEmpresa || idEmpresa <= 0) return [];
    // P-14 (2026-05-30): sempre usar /Aplicabilidade/Consulta/Configuracao, que já filtra
    // por AtualizacaoPortfoliosCEM (linhas que possuem pacote de aplicáveis). Quando o
    // projetistaId é informado, o backend restringe ainda mais às linhas com pacote desse
    // projetista. Antes, com projetistaId definido, usávamos /Projetista/ObterProjetistaPorId,
    // que listava TODAS as linhas associadas ao projetista — incluindo linhas como
    // "PRODUTIVA 25" que não têm tipologias / aplicabilidades utilizáveis.
    // Q2 (2026-07-06): a tela de Consulta passa restringirPorProjetistasEmpresa=true para,
    // SEM projetista selecionado, mostrar apenas as linhas dos projetistas liberados p/ a empresa
    // (EmpresasProjetistasConsulta). Cadastro NÃO passa a flag (mantém todas as linhas).
    const params: Record<string, number | boolean> = { idEmpresa: idEmpresa || 0 };
    if (projetistaId) params.idProjetista = projetistaId;
    if (restringirPorProjetistasEmpresa) params.restringirLinhasPorProjetistasEmpresa = true;

    const response = await api.get<any>("/Aplicabilidade/Consulta/Configuracao", { params });
    const config = response.data?.response || response.data;
    const linhas = config?.linhasSistemas ?? [];

    if (!Array.isArray(linhas) || linhas.length === 0) return [];

    return linhas.map((l: any) => ({
      value: Number(l.idLinhaSistema ?? l.IdLinhaSistema),
      label: String(l.nome ?? l.Nome ?? ""),
    })).filter((item: IFSelect) => item.value > 0 && !!item.label);
  } catch {
    return [];
  }
};

export const listarTipologias = async (projetistaId?: number, linhaId?: number): Promise<IFSelect[]> => {
  try {
    const queryParams: any = {};
    if (projetistaId) queryParams.projetistaId = projetistaId;
    if (linhaId) queryParams.linhaId = linhaId;

    const response = await api.get<any>("/Aplicabilidade/Consulta/Tipologias", { params: queryParams });
    const data = response.data?.response || response.data;
    const items = Array.isArray(data) ? data : (data?.items ?? data?.Items ?? []);

    const tipologias: IFSelect[] = [];
    for (const item of items) {
      const rawValue = item.value || item.Value || item.id || item.Id || item.idTipologia || item.IdTipologia;
      const rawLabel = item.label || item.Label || item.descricao || item.Descricao || item.nome || item.Nome || item.codigo || item.Codigo || "";

      if (rawValue != null && rawLabel) {
        const numValue = Number(rawValue);
        if (!isNaN(numValue)) {
          tipologias.push({ value: numValue, label: String(rawLabel) });
        }
      }
    }

    return tipologias;
  } catch {
    return [];
  }
};

// ===== Histórico de Importação =====

export interface IHistoricoImportacao {
  id: number;
  idEmpresa: number;
  nomeArquivo: string;
  dataImportacao: string;
  quantidadeRegistros: number;
  status: number;
  statusDescricao: string;
  podeRemover: boolean;
}

export const obterUltimaImportacao = async (idEmpresa: number): Promise<IHistoricoImportacao | null> => {
  try {
    // A API retorna diretamente o objeto, não envolto em { response: ... }
    const response = await api.get<IHistoricoImportacao | null>(
      `/Aplicabilidade/ObterUltimaImportacao?idEmpresa=${idEmpresa}`
    );
    return response.data || null;
  } catch (error) {
    console.error("Erro ao obter última importação:", error);
    return null;
  }
};

export const listarHistoricoImportacoes = async (idEmpresa: number): Promise<IHistoricoImportacao[]> => {
  try {
    console.log(`📋 [listarHistoricoImportacoes] Buscando histórico para empresa: ${idEmpresa}`);
    // A API retorna diretamente o array, não envolto em { response: ... }
    const response = await api.get<IHistoricoImportacao[]>(
      `/Aplicabilidade/ListarHistoricoImportacoes?idEmpresa=${idEmpresa}`
    );
    console.log(`📋 [listarHistoricoImportacoes] Resposta completa:`, response.data);
    console.log(`📋 [listarHistoricoImportacoes] Total de itens:`, Array.isArray(response.data) ? response.data.length : 0);
    // Retorna diretamente response.data (o array), não response.data.response
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Erro ao listar histórico de importações:", error);
    return [];
  }
};

export const removerImportacao = async (idHistorico: number, idEmpresa: number): Promise<boolean> => {
  try {
    // A API retorna diretamente o boolean, não envolto em { response: ... }
    const response = await api.delete<boolean>(
      `/Aplicabilidade/RemoverImportacao?idHistorico=${idHistorico}&idEmpresa=${idEmpresa}`
    );
    return response.data ?? false;
  } catch (error) {
    console.error("Erro ao remover importação:", error);
    throw error;
  }
};

export const downloadHistoricoImportacao = async (idHistorico: number, idEmpresa: number): Promise<void> => {
  try {
    console.log(`📥 [downloadHistoricoImportacao] Baixando histórico ${idHistorico} para empresa ${idEmpresa}`);
    const response = await api.get(
      `/Aplicabilidade/DownloadHistoricoImportacao?idHistorico=${idHistorico}&idEmpresa=${idEmpresa}`,
      { responseType: 'blob' }
    );
    
    // Extrair nome do arquivo do header Content-Disposition ou usar nome padrão
    const contentDisposition = response.headers['content-disposition'];
    let nomeArquivo = `importacao_${idHistorico}.xlsx`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        nomeArquivo = match[1].replace(/['"]/g, '');
      }
    }
    
    // Criar link de download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nomeArquivo;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    console.log(`📥 [downloadHistoricoImportacao] Download concluído: ${nomeArquivo}`);
  } catch (error) {
    console.error("Erro ao fazer download do histórico:", error);
    throw error;
  }
};

// ========== CONSULTA DE APLICABILIDADES ==========

export interface IEmpresaConsulta {
  idEmpresa: number;
  nomeFantasia: string;
  visualizacaoPadrao: string;
}

export interface IProjetistaConsulta {
  idProjetista: number;
  nome: string;
}

export interface IConfiguracaoConsulta {
  visualizacaoPadrao: string;
  referenciasDisponiveis: IReferenciaDisponivel[];
  linhasSistemas: ILinhaSistemaConsulta[];
  empresasDisponiveis: IEmpresaConsulta[];
  projetistasConfigurados: IProjetistaConsulta[];
  projetistasCadastro: IProjetistaConsulta[];
}

export interface IReferenciaDisponivel {
  idReferencia: number;
  descricao: string;
}

export interface ILinhaSistemaConsulta {
  idLinhaSistema: number;
  nome: string;
}

export interface IConsultaAplicabilidade {
  codigoPrincipal: string;
  corPrincipal?: string;
  referenciaPrincipal: string;
  idStatusPrincipal: number;
  aplicabilidades: IAplicabilidadeItem[];
  temPadrao: boolean;
  temRegraPorLinha: boolean;
  temAgrupamento: boolean;
  tipoAgrupamento?: string;
  codigosAgrupados?: string[];
}

export interface IAplicabilidadeItem {
  referencia: string;
  codigos: ICodigoAplicavelConsulta[];
}

export interface ICodigoAplicavelConsulta {
  codigo: string;
  cor?: string;
  ehAgrupamento: boolean;
  posicaoNoAgrupamento?: number;
  idStatus: number;
  // FASE 03 (itens 10/12 — 22-06): códigos que compõem o agrupamento DESTE código (coluna dinâmica),
  // para o tooltip do ícone de agrupamento na célula.
  codigosAgrupados?: string[];
}

export interface ICoberturaConsulta {
  totalCodigos: number;
  codigosComAplicabilidade: number;
  percentual: number;
}

export interface IConsultarAplicabilidadesParams {
  idEmpresa: number;
  page?: number;
  limit?: number;
  idReferencia?: number;
  idLinhaSistema?: number;
  idTipologia?: number;
  idProjetista?: number;
  busca?: string;
}

export const listarEmpresasConsulta = async (): Promise<IEmpresaConsulta[]> => {
  try {
    // Usar o mesmo endpoint da página empresasMof para garantir consistência
    const response = await api.get<any>("/Empresa/ListarEmpresas", {
      params: {
        page: 1,
        limit: 1000, // Limite alto para pegar todas as empresas
        sortBy: "NomeFantasia",
        direction: "asc",
        Filtrar: "",
      },
    });
    
    console.log("📥 [API] Resposta completa:", response);
    console.log("📥 [API] response.data:", response.data);
    
    // O CustomResponse pode retornar diretamente o PagedResponse ou dentro de { response: {...} }
    let empresasRaw: IEmpresa[] = [];
    
    if (response.data?.items && Array.isArray(response.data.items)) {
      empresasRaw = response.data.items;
    } else if (response.data?.response?.items && Array.isArray(response.data.response.items)) {
      empresasRaw = response.data.response.items;
    } else if (Array.isArray(response.data)) {
      empresasRaw = response.data;
    }
    
    console.log("📥 [API] Total de empresas retornadas:", empresasRaw.length);
    console.log("📥 [API] Primeiras 3 empresas (raw):", empresasRaw.slice(0, 3));
    
    // Verificar se os campos estão presentes
    if (empresasRaw.length > 0) {
      const primeiraEmpresa = empresasRaw[0] as any;
      console.log("📥 [API] Exemplo de empresa (todos os campos):", primeiraEmpresa);
      console.log("📥 [API] Campos específicos:", {
        idEmpresa: primeiraEmpresa.idEmpresa || primeiraEmpresa.IdEmpresa,
        nomeFantasia: primeiraEmpresa.nomeFantasia || primeiraEmpresa.NomeFantasia,
        ativo: primeiraEmpresa.ativo !== undefined ? primeiraEmpresa.ativo : primeiraEmpresa.Ativo,
        consultarAplicabilidadesHabilitado: primeiraEmpresa.consultarAplicabilidadesHabilitado !== undefined 
          ? primeiraEmpresa.consultarAplicabilidadesHabilitado 
          : primeiraEmpresa.ConsultarAplicabilidadesHabilitado,
        tipoVisualizacaoPadraoConsulta: primeiraEmpresa.tipoVisualizacaoPadraoConsulta || primeiraEmpresa.TipoVisualizacaoPadraoConsulta
      });
    }
    
    // Filtrar apenas empresas ATIVAS (removendo o filtro de ConsultarAplicabilidadesHabilitado)
    // Conforme solicitado: "todos veem todas as empresas habilitadas (independente de permissão)"
    const empresasFiltradas = empresasRaw.filter(
      (emp: any) => {
        return emp.ativo === true || emp.Ativo === true;
      }
    );
    
    console.log("📥 [API] Empresas filtradas (ativas):", empresasFiltradas.length);
    
    // Converter para IEmpresaConsulta
    const empresas: IEmpresaConsulta[] = empresasFiltradas.map((emp: IEmpresa) => ({
      idEmpresa: emp.idEmpresa,
      nomeFantasia: emp.nomeFantasia || "",
      visualizacaoPadrao: emp.tipoVisualizacaoPadraoConsulta || "Sistemista"
    }));
    
    console.log("✅ [API] Empresas processadas:", empresas);
    console.log("✅ [API] Quantidade de empresas:", empresas.length);
    
    return empresas;
  } catch (error) {
    console.error("❌ [API] Erro ao listar empresas para consulta:", error);
    return [];
  }
};

export const obterConfiguracaoConsulta = async (
  idEmpresa: number
): Promise<IConfiguracaoConsulta | null> => {
  try {
    const response = await api.get<IConfiguracaoConsulta>(
      `/Aplicabilidade/Consulta/Configuracao?idEmpresa=${idEmpresa}`
    );
    return response.data || null;
  } catch (error) {
    console.error("Erro ao obter configuração de consulta:", error);
    return null;
  }
};

export const consultarAplicabilidadesSistemista = async (
  params: IConsultarAplicabilidadesParams
): Promise<{ items: IConsultaAplicabilidade[]; page: number; totalRecords: number; totalPages: number }> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("idEmpresa", params.idEmpresa.toString());
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.idReferencia)
      queryParams.append("idReferencia", params.idReferencia.toString());
    if (params.idLinhaSistema)
      queryParams.append("idLinhaSistema", params.idLinhaSistema.toString());
    if (params.idTipologia)
      queryParams.append("idTipologia", params.idTipologia.toString());
    if (params.idProjetista)
      queryParams.append("idProjetista", params.idProjetista.toString());
    if (params.busca) queryParams.append("busca", params.busca);

    const url = `/Aplicabilidade/Consulta/Sistemista?${queryParams.toString()}`;
    console.log("📤 [API] GET (Sistemista):", url);

    const response = await api.get<any>(url);
    
    console.log("📥 [API] Response completa (Sistemista):", response);
    console.log("📥 [API] response.data (Sistemista):", response.data);
    console.log("📥 [API] Tipo de response.data (Sistemista):", typeof response.data, Array.isArray(response.data));
    
    // O CustomResponse pode retornar diretamente o PagedResponse ou dentro de { response: {...} }
    let resultado: { items: IConsultaAplicabilidade[]; page: number; totalRecords: number; totalPages: number } = { 
      items: [], 
      page: 1, 
      totalRecords: 0, 
      totalPages: 0 
    };
    
    if (response.data?.response) {
      // Formato: { response: PagedResponse<...> }
      resultado = response.data.response;
    } else if (response.data && typeof response.data === 'object' && 'items' in response.data) {
      // Formato: PagedResponse<...> diretamente
      resultado = response.data;
    } else if (Array.isArray(response.data)) {
      // Formato: Array diretamente (improvável, mas possível)
      resultado = {
        items: response.data,
        page: 1,
        totalRecords: response.data.length,
        totalPages: 1
      };
    }
    
    console.log("✅ [API] Resultado processado (Sistemista):", resultado);
    console.log("✅ [API] Total de items (Sistemista):", resultado.items?.length || 0);
    if (resultado.items && resultado.items.length > 0) {
      console.log("✅ [API] Primeiro item (Sistemista):", resultado.items[0]);
    }
    
    return resultado;
  } catch (error) {
    console.error("❌ [API] Erro ao consultar aplicabilidades (Sistemista):", error);
    if (error && typeof error === 'object' && 'response' in error) {
      console.error("❌ [API] Response do erro:", (error as any).response);
      console.error("❌ [API] Status:", (error as any).response?.status);
      console.error("❌ [API] Data do erro:", (error as any).response?.data);
    }
    return { items: [], page: 1, totalRecords: 0, totalPages: 0 };
  }
};

export const consultarAplicabilidadesFornecedor = async (
  params: IConsultarAplicabilidadesParams
): Promise<{ items: IConsultaAplicabilidade[]; page: number; totalRecords: number; totalPages: number }> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("idEmpresa", params.idEmpresa.toString());
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.idReferencia)
      queryParams.append("idReferencia", params.idReferencia.toString());
    if (params.idLinhaSistema)
      queryParams.append("idLinhaSistema", params.idLinhaSistema.toString());
    if (params.idTipologia)
      queryParams.append("idTipologia", params.idTipologia.toString());
    if (params.idProjetista)
      queryParams.append("idProjetista", params.idProjetista.toString());
    if (params.busca) queryParams.append("busca", params.busca);

    const url = `/Aplicabilidade/Consulta/Fornecedor?${queryParams.toString()}`;
    console.log("📤 [API] GET (Fornecedor):", url);

    const response = await api.get<any>(url);
    
    console.log("📥 [API] Response completa (Fornecedor):", response);
    console.log("📥 [API] response.data (Fornecedor):", response.data);
    console.log("📥 [API] Tipo de response.data (Fornecedor):", typeof response.data, Array.isArray(response.data));
    
    // O CustomResponse pode retornar diretamente o PagedResponse ou dentro de { response: {...} }
    let resultado: { items: IConsultaAplicabilidade[]; page: number; totalRecords: number; totalPages: number } = { 
      items: [], 
      page: 1, 
      totalRecords: 0, 
      totalPages: 0 
    };
    
    if (response.data?.response) {
      // Formato: { response: PagedResponse<...> }
      resultado = response.data.response;
    } else if (response.data && typeof response.data === 'object' && 'items' in response.data) {
      // Formato: PagedResponse<...> diretamente
      resultado = response.data;
    } else if (Array.isArray(response.data)) {
      // Formato: Array diretamente (improvável, mas possível)
      resultado = {
        items: response.data,
        page: 1,
        totalRecords: response.data.length,
        totalPages: 1
      };
    }
    
    console.log("✅ [API] Resultado processado (Fornecedor):", resultado);
    console.log("✅ [API] Total de items (Fornecedor):", resultado.items?.length || 0);
    if (resultado.items && resultado.items.length > 0) {
      console.log("✅ [API] Primeiro item (Fornecedor):", resultado.items[0]);
    }
    
    return resultado;
  } catch (error) {
    console.error("❌ [API] Erro ao consultar aplicabilidades (Fornecedor):", error);
    if (error && typeof error === 'object' && 'response' in error) {
      console.error("❌ [API] Response do erro:", (error as any).response);
      console.error("❌ [API] Status:", (error as any).response?.status);
      console.error("❌ [API] Data do erro:", (error as any).response?.data);
    }
    return { items: [], page: 1, totalRecords: 0, totalPages: 0 };
  }
};

export const obterCoberturaConsulta = async (
  idEmpresa: number,
  filtros?: {
    tipoVisao?: "Sistemista" | "Fornecedor";
    idProjetista?: number;
    idLinhaSistema?: number;
    idTipologia?: number;
    busca?: string;
  }
): Promise<ICoberturaConsulta | null> => {
  try {
    console.log("📊 [API] Buscando cobertura para empresa:", idEmpresa, "filtros:", filtros);
    // P-04: encaminha filtros do grid para recalcular cobertura coerente com o universo filtrado.
    const params = new URLSearchParams({ idEmpresa: String(idEmpresa) });
    if (filtros?.tipoVisao) params.append("tipoVisao", filtros.tipoVisao);
    if (filtros?.idProjetista != null) params.append("idProjetista", String(filtros.idProjetista));
    if (filtros?.idLinhaSistema != null) params.append("idLinhaSistema", String(filtros.idLinhaSistema));
    if (filtros?.idTipologia != null) params.append("idTipologia", String(filtros.idTipologia));
    if (filtros?.busca) params.append("busca", filtros.busca);

    const response = await api.get<any>(
      `/Aplicabilidade/Consulta/Cobertura?${params.toString()}`
    );
    
    console.log("📊 [API] Resposta completa de cobertura:", response);
    console.log("📊 [API] response.data:", response.data);
    
    // O CustomResponse pode retornar diretamente ou dentro de { response: {...} }
    let coberturaData: ICoberturaConsulta | null = null;
    
    if (response.data?.response) {
      coberturaData = response.data.response;
    } else if (response.data && typeof response.data === 'object' && 'totalCodigos' in response.data) {
      coberturaData = response.data;
    } else if (Array.isArray(response.data) && response.data.length > 0) {
      coberturaData = response.data[0];
    }
    
    console.log("📊 [API] Cobertura processada:", coberturaData);
    
    if (coberturaData) {
      console.log("✅ [API] Cobertura encontrada:", {
        totalCodigos: coberturaData.totalCodigos,
        codigosComAplicabilidade: coberturaData.codigosComAplicabilidade,
        percentual: coberturaData.percentual
      });
    } else {
      console.warn("⚠️ [API] Cobertura não encontrada ou vazia");
    }
    
    return coberturaData;
  } catch (error) {
    console.error("❌ [API] Erro ao obter cobertura de consulta:", error);
    return null;
  }
};

export const exportarConsulta = async (
  params: IConsultarAplicabilidadesParams,
  tipoVisao: string
): Promise<void> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("idEmpresa", params.idEmpresa.toString());
    queryParams.append("tipoVisao", tipoVisao);
    if (params.idReferencia)
      queryParams.append("idReferencia", params.idReferencia.toString());
    if (params.idLinhaSistema)
      queryParams.append("idLinhaSistema", params.idLinhaSistema.toString());
    if (params.idTipologia)
      queryParams.append("idTipologia", params.idTipologia.toString());
    if (params.idProjetista)
      queryParams.append("idProjetista", params.idProjetista.toString());
    if (params.busca) queryParams.append("busca", params.busca);

    const response = await api.get(`/Aplicabilidade/Consulta/Exportar?${queryParams.toString()}`, {
      responseType: "blob",
      timeout: 300000,
    });

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `CONSULTA_APLICABILIDADES_${params.idEmpresa}_${new Date().toISOString().split("T")[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  } catch (error) {
    console.error("Erro ao exportar consulta:", error);
    throw error;
  }
};

